import { SelectProps } from "@radix-ui/react-select";
import classNames from "classnames";
import { FC, useId } from "react";
import InputLabel from "../components/label";
import { InputWrapper } from "../components/wrapper";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../select/core";
import { InputTargetType } from "../types";
import { countryCodes } from "./country-codes";

export type FinalTextType = {
  title: string;
  value: string;
};

export interface CountrySelectType extends SelectProps {
  icon?: React.ReactNode | string;
  onChange?: (e: InputTargetType) => void;
  containerClassName?: string;
  labelClassName?: string;
  wrapperClassName?: string;
  id?: string;
  label?: string;
  className?: string;
  placeholder?: string;
  dialCode?: boolean;
  comboInput?: boolean;
}

const CountrySelect: FC<CountrySelectType> = ({
  id,
  className,
  containerClassName,
  wrapperClassName,
  labelClassName,
  label,
  icon,
  placeholder,
  value,
  defaultValue,
  onChange,
  name = "countrySelect",
  dialCode,
  comboInput,
  ...rest
}) => {
  const uniqueId = useId();

  const handleChange = (val: string) => {
    try {
      if (onChange) {
        onChange({
          target: {
            value: val,
            name: name,
          },
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const finalText = (country: (typeof countryCodes)[0]): FinalTextType => {
    let title = "";
    let value = "";

    try {
      if (dialCode) {
        title = `${country?.dialCode}-${country?.name}`;
        value = country?.dialCode;
      } else {
        title = country?.name;
        value = country?.name;
      }

      return { title, value };
    } catch (error) {
      console.error(error);
      return { title, value };
    }
  };

  const current = countryCodes?.find(
    (country) => country?.dialCode === (value ?? defaultValue)
  );

  return (
    <Select
      onValueChange={handleChange}
      defaultValue={defaultValue}
      value={value}
      name={name}
      {...rest}
    >
      <div
        className={classNames(
          "flex flex-col gap-1.5 items-center [&>*]:w-full",
          containerClassName
        )}
      >
        {label && (
          <InputLabel htmlFor={id ?? uniqueId} className={labelClassName}>
            {label}
          </InputLabel>
        )}
        <SelectTrigger
          className={classNames(
            "w-full !shadow-none border-light !ring-0",
            comboInput &&
              "!h-max !border-none overflow-hidden !rounded-none !shadow-none !p-0",
            className
          )}
        >
          <InputWrapper
            icon={icon}
            className={classNames(
              "w-full border-none !bg-transparent !h-max !p-0",
              comboInput && "!p-0 !w-max !shadow-none !rounded-none",
              wrapperClassName
            )}
          >
            <SelectValue
              title={String(value)}
              placeholder={placeholder ?? "Select"}
              className="!text-clip"
            >
              <div className="flex items-center gap-1 w-full">
                <img
                  src={current?.flag}
                  alt="flag"
                  className="max-w-4 w-4 h-auto object-contain"
                />
                <span className="w-full text-xs whitespace-nowrap text-ellipsis overflow-hidden">
                  {current?.isoCode}
                </span>
              </div>
            </SelectValue>
          </InputWrapper>
        </SelectTrigger>
      </div>

      <SelectContent className="bg-white w-full max-w-64 overflow-y-auto">
        <SelectGroup>
          {countryCodes?.map((country, index) => {
            return (
              <SelectItem
                key={index}
                title={finalText(country)?.title}
                value={finalText(country)?.value}
                className="cursor-pointer hover:bg-slate-100"
              >
                <div className="flex items-center gap-1 w-full">
                  <img
                    src={country?.flag}
                    alt="flag"
                    className="max-w-5 w-5 h-auto object-contain"
                  />
                  <span className="w-full whitespace-nowrap text-ellipsis overflow-hidden ">
                    {finalText(country)?.title}
                  </span>
                </div>
              </SelectItem>
            );
          })}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
};

export default CountrySelect;
